<template>
    <div v-show="visible" class="w3-modal" style="display: block;">
        <div class="w3-modal-content">
            <header>
                <div class="w3-cell-row">
                    <div class="w3-cell w3-cell-middle">
                        <h2>{{ mode === 'edit' ? $t('Edit token') : $t('Create token') }}</h2>
                    </div>
                    <div class="w3-cell w3-right">
                        <a class="close-button" @click="close()">&times;</a>
                    </div>
                </div>
                <hr />
            </header>

            <div class="twi-modal-body twi-form">
                <div class="w3-cell-row">
                    <div class="w3-cell w3-cell-middle" style="width: 10%;">{{ $t('Token') }}:</div>
                    <div class="w3-cell">
                        <input class="w3-input monospace" v-model="token.token" :readonly="mode === 'edit'">
                    </div>
                    <div v-if="mode === 'create'" class="w3-cell w3-cell-middle w3-right-align" style="width: 50px;">
                        <button class="w3-button twi-button w3-small" @click="setRandomToken()">
                            <img src="@/assets/img/dice-solid-white-16.png" :alt="$t('Generate random token')" :title="$t('Generate random token')">
                        </button>
                    </div>
                </div>
                <div v-if="mode === 'create'" class="w3-cell-row">
                    <div class="w3-cell w3-cell-middle" style="width: 10%;">&nbsp;</div>
                    <div class="w3-cell">
                        <span v-show="tokenValidationResult === 'loading'"><img src="@/assets/img/spinner-green-24.gif" :alt="$t('Loading')"></span>
                        <span v-show="tokenValidationResult === 'free'" class="w3-text-green w3-small">{{ $t('The token does not yet exist.') }}</span>
                        <span v-show="tokenValidationResult === 'exists'" class="w3-text-red w3-small">{{ $t('The token does exist and can not be used.') }}</span>
                        <span v-show="tokenValidationResult === 'unknown'" class="w3-text-orange w3-small">{{ $t('It is not known whether the token already exists or not.') }}</span>
                        <span v-show="tokenValidationResult === 'contents'" class="w3-text-orange w3-small">{{ $t('The entered token must consist of 24 lower case letters and numbers.') }}</span>
                    </div>
                </div>
                <div v-if="mode === 'edit'" class="w3-cell-row">
                    <div class="w3-cell w3-cell-middle" style="width: 10%;">{{ $t('Created') }}:</div>
                    <div class="w3-cell">{{ token.created | datetime_format }}</div>
                </div>
                <div v-if="mode === 'edit'" class="w3-cell-row">
                    <div class="w3-cell w3-cell-middle" style="width: 10%;">{{ $t('Modified') }}:</div>
                    <div class="w3-cell">{{ modified }}</div>
                </div>
                <div class="w3-cell-row">
                    <div class="w3-cell w3-cell-middle" style="width: 10%;">{{ $t('Note') }}:</div>
                    <div class="w3-cell"><input class="w3-input" v-model="token.note" /></div>
                </div>
                <div v-if="mode === 'edit'" class="w3-cell-row">
                    <div class="w3-cell w3-cell-middle" style="width: 10%;">{{ $t('Domains') }}:</div>
                    <div class="w3-cell">
                        <span v-show="token.domains.length === 0">{{ $t('None') }}</span>
                        <span style="margin-right: 10px;" v-for="d in token.domains" :key="d.domain">{{ d.domain }}</span>
                    </div>
                </div>
            </div>

            <footer class="w3-cell-row">
                <div class="w3-cell w3-cell-middle" style="width: 50%;">
                    <span v-show="action !== ''">
                        <img src="@/assets/img/spinner-green-32.gif" :alt="$t('Loading')"> {{ action }}
                    </span>
                </div>
                <div class="w3-cell w3-right-align twi-button-bar">
                    <button v-if="mode === 'edit'" class="w3-button twi-button w3-hover-red" @click="deleteToken()">{{ $t('Delete token') }}</button>
                    <button class="w3-button twi-button" @click="saveToken()">{{ $t('Save token') }}</button>
                    <button class="w3-button twi-button" @click="close()">{{ $t('Cancel') }}</button>
                </div>
            </footer>
        </div>
    </div>
</template>

<style scoped>

</style>

<script>
import moment from 'moment'

import Api from 'Api'
import DateTime from '@/utils/DateTime'

import Token from '@/model/Token'

export default {
    data () {
        return {
            visible: false,
            mode: 'create',
            action: '',

            token: new Token(),
            tokenChars: 'abcdefghijklmnopqrstuvwxyz01234567890',
            tokenRx: /^[a-z0-9]+$/,
            tokenValidationResult: 'loading',
            tokenValidationTimeout: null
        }
    },

    computed: {
        modified: function() {
            if (this.token.modified === null) {
                return this.$i18n.t('N/A')
            }
            return DateTime.format(this.token.modified, null, DateTime.getDateTimeFormat())
        }
    },

    watch: {
        'token.token': function (n) {
            let self = this
            if (self.tokenValidationTimeout !== null) {
                window.clearTimeout(self.tokenValidationTimeout)
            }
            self.tokenValidationTimeout = window.setTimeout(() => {
                self.checkValidToken()
                self.tokenValidationTimeout = null
            }, 400)
        }
    },

    methods: {
        close () {
            this.visible = false
        },

        showEdit (token) {
            if (typeof token !== 'object') {
                return
            }
            
            this.mode = 'edit'
            this.token = Token.fromObject(token)
            this.visible = true
        },

        showCreate () {
            this.mode = 'create'
            this.token = new Token()
            this.setRandomToken()
            this.visible = true
        },

        setRandomToken (len = 24) {
            if (this.mode === 'edit') {
                return
            }

            let res = ''
            let maxidx = this.tokenChars.length - 1
            for (let i = 0; i < len; ++i) {
                let idx = Math.floor((Math.random() * 100) % maxidx)
                res += this.tokenChars[idx]
            }

            this.token.token = res
        },

        checkValidToken () {
            let self = this
            self.tokenValidationResult = 'loading'

            if (self.token.token.length !== 24 || self.tokenRx.test(self.token.token) === false) {
                self.tokenValidationResult = 'contents'
                return
            }

            Api.postTokensExists(self.token.token)
                .then(resp => {
                    if (typeof resp.data !== 'boolean') {
                        self.tokenExists = 'unknown'
                        throw new Error()
                    }

                    self.tokenValidationResult = resp.data ? 'exists' : 'free'
                })
                .catch(() => {
                    window.toast({
                        text: self.$i18n.t('Failed to check if the entered token already exists.'),
                        color: 'yellow'
                    })
                })
        },

        saveToken () {
            if (this.mode === 'create') {
                this.saveCreateToken()
            } else {
                this.saveEditToken()
            }
        },

        saveCreateToken () {
            let self = this

            if (self.mode !== 'create') {
                return
            }

            self.action = self.$i18n.t('Creating new token {0} ...', [self.token.token])
            Api.putTokens(self.token.token, self.token.note)
                .then(resp => {
                    if (typeof resp.data !== 'string') {
                        window.toast({
                            text: self.$i18n.t('Invalid API response.'),
                            color: 'red'
                        })
                        return
                    }

                    try {
                        moment(resp.data)
                        self.token.created = resp.data
                        self.$emit('inserted', { token: Token.fromObject(self.token) })
                        window.toast({
                            text: self.$i18n.t('Token has been created successfully.'),
                            color: 'green'
                        })
                    } catch (_err) {
                        window.toast({
                            text: self.$i18n.t('The token has been created but the API response could not be parsed. Trying to reload the tokens list. If the token does not appear, try reloading the page.'),
                            color: 'yellow',
                            timeout: 5000
                        })
                        self.$emit('force-reload')
                    }

                    self.close()
                })
                .catch(err => {
                    if (typeof err.response !== 'object' || typeof err.response.status !== 'number') {
                        window.toast({
                            text: self.$i18n.t('Invalid API response.'),
                            color: 'red'
                        })
                        return
                    }

                    let message = 'Invalid API response.'
                    switch (err.response.status) {
                    case 400:
                        message = 'The entered token value is invalid or does already exist.'
                        break

                    case 403:
                        message = 'You are not authorized to create tokens.'
                        break

                    default:
                        // pass
                    }

                    window.toast({
                        text: self.$i18n.t(message),
                        color: 'red'
                    })
                })
                .finally(() => self.action = '')
        },

        saveEditToken () {
            let self = this

            if (self.mode !== 'edit') {
                return
            }

            self.action = self.$i18n.t('Updating token {0} ...', [self.token.token])
            Api.postTokens(self.token.token, self.token.note)
                .then(resp => {
                    if (typeof resp.data !== 'string') {
                        window.toast({
                            text: self.$i18n.t('Invalid API response.'),
                            color: 'red'
                        })
                        return
                    }

                    try {
                        moment(resp.data)
                        self.token.modified = resp.data
                        self.$emit('updated', { token: Token.fromObject(self.token) })
                        self.close()
                        window.toast({
                            text: self.$i18n.t('Token has been updated.'),
                            color: 'green'
                        })
                    } catch (_err) {
                        window.toast({
                            text: self.$i18n.t('The token was successfully updated, but the API response could not be parsed. Please refresh the page.'),
                            color: 'yellow',
                            timeout: 5000
                        })
                    }
                })
                .catch(err => {
                    if (typeof err.response.status !== 'number') {
                        window.toast({
                            text: self.$i18n.t('Invalid API response.'),
                            color: 'red'
                        })
                        return
                    }

                    switch (err.response.status) {
                    case 403:
                        window.toast({
                            text: self.$i18n.t('You are not authorized to update this token.'),
                            color: 'red'
                        })
                        break
                    
                    case 404:
                        window.toast({
                            text: self.$i18n.t('The token could not be found.'),
                            color: 'red'
                        })
                        break
                    
                    default:
                        window.toast({
                            text: self.$i18n.t('Failed to update the token.'),
                            color: 'red'
                        })
                    }
                })
                .finally(() => self.action = '')
        },

        deleteToken () {
            let self = this

            if (self.mode !== 'edit') {
                return
            }

            let yn = confirm(self.$i18n.t('Do you really want to delete this token? This action can not be undone!'))
            if (!yn) {
                return
            }

            self.action = self.$i18n.t('Deleting token {0} ...', [self.token.token])
            Api.deleteTokens(self.token.token)
                .then(resp => {
                    window.toast({
                        text: self.$i18n.t('The token has been deleted.'),
                        color: 'green'
                    })
                    self.$emit('deleted', { token: self.token })
                    self.close()
                })
                .catch(err => {
                    if (typeof err.response.status !== 'number') {
                        window.toast({
                            text: self.$i18n.t('Invalid API response.'),
                            color: 'red'
                        })
                        return
                    }

                    switch (err.response.status) {
                    case 400:
                        window.toast({
                            text: self.$i18n.t('Can not delete a token with associated domains.'),
                            color: 'red'
                        })
                        break

                    case 403:
                        window.toast({
                            text: self.$i18n.t('You are not authorized to delete this token.'),
                            color: 'red'
                        })
                        break
                    
                    case 404:
                        self.$emit('deleted', { token: self.token })
                        self.close()
                        break
                    
                    default:
                        window.toast({
                            text: self.$i18n.t('Failed to delete the token.'),
                            color: 'red'
                        })
                    }
                })
                .finally(() => self.action = '')
        }
    }
}
</script>

<i18n src="@/lang/common.json"></i18n>
<i18n src="@/lang/tokens.json"></i18n>
